/** Logic ported from ValidiateChat chat.html */

import {
  GATE0_REGISTRY,
  gate0FieldForCanonicalText,
  isTextCanonicalForGate0Field,
} from "../pages/framework/gate0Registry";

export function sectionRequiredFromItems(items) {
  return (items || []).length > 0 && items.some((it) => it.required !== false);
}

/** Section is required for intake when at least one question is mandatory (toggle on). */
export function isSectionRequiredForIntake(section) {
  if (!section) return false;
  return sectionRequiredFromItems(section.items);
}

export const SECTION_ICONS = {
  identity: "🏷",
  purpose: "🎯",
  users: "👥",
  architecture: "🏗",
  data: "🗄",
  decision: "⚖",
  consequence: "⚡",
  regulatory: "📋",
  operational: "⚙",
  controls: "🛡",
  success: "📊",
  governance: "🏛",
};

export function itemRequiredForIntake(item) {
  return item?.required !== false;
}

export function sectionHasUncapturedMandatory(section, captured) {
  return (section?.items || []).some(
    (it) => itemRequiredForIntake(it) && !captured?.[it.id]
  );
}

/** Mirror agent compute_scores_from_captured for client-side progress sync. */
/** Keep captured entries that still belong to the active framework. */
export function filterCapturedToFrameworkItems(captured, sections) {
  const ids = new Set();
  (sections || []).forEach((s) => {
    (s.items || []).forEach((it) => {
      if (it?.id) ids.add(it.id);
    });
  });
  return Object.fromEntries(
    Object.entries(captured || {}).filter(([k]) => ids.has(k))
  );
}

/** Count filled items in a section, excluding stale unlinked captures. */
export function countSectionCaptured(captured, section, sectionId = section?.id) {
  return (section?.items || []).filter((it) => {
    const cap = captured?.[it.id];
    return cap && !isStaleUnlinkedCapture(it, cap, sectionId);
  }).length;
}

/** Item completion percentage for one section (0–100), aligned with sidebar progress. */
export function computeSectionCompletionPct(captured, section, sectionId = section?.id) {
  const items = section?.items || [];
  if (!items.length) return 0;
  const cap = countSectionCaptured(captured, section, sectionId);
  return Math.min(100, Math.round((cap / items.length) * 100));
}

/** Per-section item completion percentages keyed by section id. */
export function computeSectionCompletionFromCaptured(captured, sections) {
  const out = {};
  (sections || []).forEach((s) => {
    out[s.id] = computeSectionCompletionPct(captured, s, s.id);
  });
  return out;
}

export function computeScoresFromCaptured(captured, sections) {
  const scores = {};
  (sections || []).forEach((s) => {
    const items = s.items || [];
    scores[s.id] = 0;
    if (!items.length) return;

    const threshold = Number(s.threshold) || 85;
    const total = items.length;
    const cap = captured || {};
    const covered = items.filter((it) => cap[it.id]).length;
    const explicitRequired = items.filter((it) => itemRequiredForIntake(it)).length;
    let totalRequired;
    let requiredCaptured;
    if (explicitRequired) {
      totalRequired = explicitRequired;
      requiredCaptured = items.filter(
        (it) => itemRequiredForIntake(it) && cap[it.id]
      ).length;
    } else {
      totalRequired = total;
      requiredCaptured = covered;
    }

    if (requiredCaptured >= totalRequired) {
      const optionalTotal = Math.max(total - totalRequired, 1);
      const optionalCovered = covered - requiredCaptured;
      const optionalBonus = Math.round((optionalCovered / optionalTotal) * 5);
      scores[s.id] = Math.min(threshold + optionalBonus, 100);
    } else {
      const reqPct = requiredCaptured / totalRequired;
      const optPct = (covered - requiredCaptured) / Math.max(total - totalRequired, 1);
      const raw = Math.round(reqPct * (threshold - 5) + optPct * 5);
      scores[s.id] = Math.min(threshold - 1, Math.max(0, raw));
    }
  });
  return scores;
}

export function computeIsComplete(scores, sections, captured = {}) {
  const required = (sections || []).filter((s) => isSectionRequiredForIntake(s));
  if (!required.length) return false;
  return required.every((s) => {
    if ((scores[s.id] || 0) < Number(s.threshold || 85)) return false;
    if (sectionHasUncapturedMandatory(s, captured)) return false;
    return true;
  });
}

/** Weighted mean of section confidence scores (0–100), not threshold-completion %. */
export function computeOverallScore(scores, sections) {
  if (!sections?.length) return 0;
  const tw = sections.reduce((a, s) => a + (s.weight || 1), 0);
  if (!tw) return 0;
  const ws = sections.reduce(
    (a, s) => a + (scores[s.id] || 0) * (s.weight || 1),
    0
  );
  return Math.round((ws / tw) * 10) / 10;
}

export function getNextSection(scores, sections, captured = {}) {
  const required = (sections || []).filter(
    (s) =>
      isSectionRequiredForIntake(s) &&
      ((scores[s.id] || 0) < Number(s.threshold || 85)
        || sectionHasUncapturedMandatory(s, captured))
  );
  if (!required.length) return null;
  return required.reduce((a, b) =>
    (scores[a.id] || 0) / Number(a.threshold || 85) <
    (scores[b.id] || 0) / Number(b.threshold || 85)
      ? a
      : b
  );
}

export function barColor(score, threshold) {
  const thr = Number(threshold) || 85;
  if (score >= thr) return "#16a34a";
  if (score >= thr * 0.65) return "#b45309";
  return "#dc2626";
}

export function getScoreChanges(prevScores, nextScores, sections) {
  const changes = [];
  Object.entries(nextScores || {}).forEach(([id, newScore]) => {
    const oldScore = prevScores?.[id] || 0;
    const diff = newScore - oldScore;
    if (diff === 0) return;
    const sec = sections.find((s) => s.id === id);
    if (sec) {
      changes.push({
        id,
        name: sec.name,
        diff,
        score: newScore,
        threshold: Number(sec.threshold) || 85,
      });
    }
  });
  return changes.sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff));
}

/** Section completion pill deltas from captured item counts (matches sidebar %). */
export function getSectionCompletionChanges(prevCaptured, nextCaptured, sections) {
  const byId = new Map();
  (sections || []).forEach((s) => {
    if (!s?.id || byId.has(s.id)) return;
    const score = computeSectionCompletionPct(nextCaptured, s, s.id);
    const prev = computeSectionCompletionPct(prevCaptured, s, s.id);
    const diff = score - prev;
    if (diff === 0) return;
    byId.set(s.id, {
      id: s.id,
      name: s.name,
      diff,
      score,
      threshold: 100,
    });
  });
  return [...byId.values()].sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff));
}

/** True for paste/upload system messages — not editable user turns. */
export function isSystemUserMessage(message) {
  const text = message?.content || "";

  if (String(message?.id || "").startsWith("upload-")) {
    return true;
  }

  return /^(📋|📄)/.test(text);
}

/** Resolve 1-based turn number for a user message (for rewind). */
export function resolveMessageTurn(messages, index) {
  const msg = messages[index];
  if (msg?.turn != null) return msg.turn;
  let turn = 0;
  for (let i = 0; i < index; i += 1) {
    const m = messages[i];
    if (m.role !== "user" || isSystemUserMessage(m)) continue;
    turn += 1;
  }
  return turn + 1;
}

export function messagesToTextHistory(messages) {
  return (messages || [])
    .filter((m) => (m.role === "user" || m.role === "assistant") && !m.outputs)
    .map((m) => ({
      role: m.role,
      content: m.role === "assistant" ? stripApologyBoilerplate(m.content) : (m.content || ""),
    }))
    .filter((m) => m.content.trim());
}

const WELCOME_LABELS = new Set(["welcome", "start", "resume"]);

/** Welcome/start/resume only. Repeated answers and refusals are real turns. */
export function isWelcomeTranscriptMessage(message) {
  const label = String(message?.label || "").trim().toLowerCase();
  if (WELCOME_LABELS.has(label)) return true;
  const id = String(message?.id || "");
  return id.startsWith("welcome-") || id.startsWith("resume-");
}

/**
 * Drop a second copy of the welcome bubble (config screen and chat both
 * insert it). Leave every other message, even when the text matches a
 * previous turn — a repeated answer or the same refusal is a new turn.
 */
export function dedupeIntakeTranscript(messages) {
  const seenWelcome = new Set();
  return (messages || []).filter((message) => {
    if (!isWelcomeTranscriptMessage(message)) return true;
    const key = `${String(message?.role || "").trim().toLowerCase()}:${String(message?.content || "").trim()}`;
    if (seenWelcome.has(key)) return false;
    seenWelcome.add(key);
    return true;
  });
}

/**
 * Prefer the local list only when it still contains every server message
 * and has turns the server has not stored. An edit saves a shorter list
 * with new ids, so that server list wins and the removed tail stays removed.
 * Callers must pass local messages only when they belong to this session.
 */
export function selectIntakeTranscriptSource(serverMessages, localMessages) {
  const server = Array.isArray(serverMessages) ? serverMessages : [];
  const local = Array.isArray(localMessages) ? localMessages : [];
  if (!server.length) return local;
  if (!local.length) return server;
  const serverIds = new Set(server.map((message) => message?.id).filter((id) => id != null));
  const localHasEveryServerId = server.every((message) => message?.id != null && local.some((item) => item?.id === message.id));
  const localHasMessageServerLacks = local.some((message) => message?.id != null && !serverIds.has(message.id));
  if (localHasEveryServerId && localHasMessageServerLacks) return local;
  return server;
}

function isUploadCancelNote(message, filename) {
  const label = String(message?.label || "");
  const content = String(message?.content || "");
  const isCancel = label === "Upload cancelled" || content.includes("Upload cancelled");
  if (!isCancel) return false;
  if (!filename) return true;
  return content.includes(filename) || label.includes(filename);
}

function isUploadLine(message, filename) {
  const role = String(message?.role || "").trim().toLowerCase();
  if (role !== "user") return false;
  const id = String(message?.id || "");
  const content = String(message?.content || "");
  if (id.startsWith("upload-")) {
    return !filename || content.includes(filename);
  }
  return Boolean(filename) && content.includes(filename);
}

/**
 * True when this filename already has a cancel note after its latest upload line.
 * The note counts by its label or by "Upload cancelled" in the text, so a
 * reopened transcript still matches after the upload id was not stored.
 * A later upload of the same file with no note after it returns false.
 */
export function transcriptHasUploadCancelled(messages, filename) {
  const list = messages || [];
  const name = filename || "";
  let lastUploadIdx = -1;
  for (let i = 0; i < list.length; i += 1) {
    if (isUploadLine(list[i], name)) lastUploadIdx = i;
  }
  const afterUpload = lastUploadIdx < 0 ? list : list.slice(lastUploadIdx + 1);
  return afterUpload.some((message) => isUploadCancelNote(message, name));
}

const OFFTOPIC_REFUSAL = "I can only help with the AI-system intake";

/**
 * Chat turns show a section pill only when this turn captured items.
 * Document extraction keeps the pill. A guardrail refusal, an off-topic
 * reply, or any non-capture routing outcome updates captured quietly.
 * An older payload with no routing_outcome still pills, except those refusals.
 */
export function shouldAttachSectionScorePill({
  fromExtraction = false,
  routingOutcome,
  capturedIds,
  guardrail,
  reply,
} = {}) {
  if (fromExtraction) return true;
  const text = String(reply || "");
  const refusal =
    Boolean(guardrail)
    || text.includes(OFFTOPIC_REFUSAL);
  if (routingOutcome == null || routingOutcome === "") {
    return !refusal;
  }
  if (routingOutcome !== "CAPTURE_PENDING" && routingOutcome !== "CAPTURE_OPEN") {
    return false;
  }
  return Array.isArray(capturedIds) && capturedIds.some(Boolean);
}

/** User turn that started a document extraction (upload chip or paste chip). */
export function isExtractionSourceMessage(message) {
  const role = String(message?.role || "").trim().toLowerCase();
  if (role !== "user") return false;
  const id = String(message?.id || "");
  if (id.startsWith("upload-") || id.startsWith("paste-")) return true;
  const content = String(message?.content || "");
  return content.startsWith("Uploading ")
    || content.includes("Processing pasted document");
}

/**
 * Message the extraction pill belongs under: the assistant reply for that
 * upload/paste, or the upload/paste line when no reply was stored.
 * Later chat turns must not become this anchor.
 */
export function extractionPillAnchorId(messages) {
  const list = messages || [];
  let sourceIdx = -1;
  for (let i = 0; i < list.length; i += 1) {
    if (isExtractionSourceMessage(list[i])) sourceIdx = i;
  }
  if (sourceIdx < 0) return null;
  const next = list[sourceIdx + 1];
  const nextRole = String(next?.role || "").trim().toLowerCase();
  if (next && nextRole === "assistant" && !next.isError) return next.id || null;
  return list[sourceIdx].id || null;
}

/**
 * Stamp an anchor onto extracted pills that were saved before pinning existed.
 * Returns the same array when every extracted pill already has an anchor.
 */
export function withExtractionPillAnchors(messages, scorePills) {
  const list = scorePills || [];
  const anchorMessageId = extractionPillAnchorId(messages);
  if (!anchorMessageId) return list;
  let changed = false;
  const next = list.map((pill) => {
    if (!pill || pill.prefix !== "Extracted" || pill.anchorMessageId) return pill;
    changed = true;
    return { ...pill, anchorMessageId };
  });
  return changed ? next : list;
}

/**
 * Split score pills into those that render after a specific message and those
 * that still have no place in the transcript.
 * A pill already stored on a message is omitted so it is not drawn twice.
 * An extracted pill stays on its anchor. A chat pill with no anchor stays
 * at the end of the transcript.
 */
export function placeScorePills(messages, scorePills) {
  const list = messages || [];
  const attachedIds = new Set(list.map((message) => message?.scorePill?.id).filter(Boolean));
  const messageIds = new Set(list.map((message) => message?.id).filter(Boolean));
  const byMessageId = new Map();
  const trailing = [];

  const push = (messageId, pill) => {
    const existing = byMessageId.get(messageId) || [];
    existing.push(pill);
    byMessageId.set(messageId, existing);
  };

  (scorePills || []).forEach((pill) => {
    if (!pill?.id || attachedIds.has(pill.id)) return;
    if (pill.anchorMessageId && messageIds.has(pill.anchorMessageId)) {
      push(pill.anchorMessageId, pill);
      return;
    }
    if (pill.prefix === "Extracted") {
      const anchorId = extractionPillAnchorId(list);
      if (anchorId) {
        push(anchorId, pill);
        return;
      }
    }
    trailing.push(pill);
  });

  return { byMessageId, trailing };
}

/** Pill text: current section score with optional up/down delta. */
export function formatScoreChangePill(change) {
  const suffix = change.score >= change.threshold ? " ✓" : "";
  let delta = "";
  if (change.diff > 0) {
    delta = ` <span style="font-weight:700;color:#197F5C">(+${change.diff}%)</span>`;
  } else if (change.diff < 0) {
    delta = ` <span style="font-weight:700;color:#dc2626">(${change.diff}%)</span>`;
  }
  return `${change.name} <strong>${change.score}%</strong>${delta}${suffix}`;
}

/** Build structured section completion rows for expandable chat pills. */
export function buildSectionScoreChanges(sections, captured, prevCaptured = {}) {
  return (sections || []).map((s) => {
    const score = computeSectionCompletionPct(captured, s, s.id);
    const diff = score - computeSectionCompletionPct(prevCaptured, s, s.id);
    return {
      id: s.id,
      name: s.name,
      score,
      diff,
      threshold: 100,
    };
  });
}

/** All framework sections with current completion (deltas only where changed). */
export function formatAllSectionScoresPill(sections, captured, prevCaptured = {}) {
  return buildSectionScoreChanges(sections, captured, prevCaptured)
    .map(formatScoreChangePill)
    .join(" · ");
}

export function formatExtractionProgressPill({
  capturedCount,
  factsFilled,
  factsTotal,
  overallPct,
  improvedSections = [],
}) {
  if (improvedSections.length) {
    const parts = improvedSections.slice(0, 4).map(formatScoreChangePill);
    const more =
      improvedSections.length > 4 ? ` · +${improvedSections.length - 4} more` : "";
    return `Extracted · ${parts.join(" · ")}${more}`;
  }
  const factsLabel =
    factsTotal > 0 ? `${factsFilled}/${factsTotal} facts` : `${capturedCount} facts`;
  return `${factsLabel} captured from document · profile now <strong>${overallPct}%</strong>`;
}

export function stripApologyBoilerplate(text) {
  if (!text) return "";
  const normalized = text
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201c\u201d]/g, '"')
    .trim();
  const isApologySentence = (sentence) => {
    const t = sentence.trim().toLowerCase();
    if (!t) return true;
    if (t.startsWith("it seems there")) return true;
    if (t.startsWith("i encountered an issue")) return true;
    if (t.startsWith("however, i have noted") || t.startsWith("however i have noted")) return true;
    return (
      t.includes("issue processing")
      || t.includes("technical issue")
      || t.includes("invalid_request_error")
      || t.includes("invalid parameter")
      || t.includes("messages with role 'tool'")
      || t.includes("try that again")
      || (t.includes("try again") && t.length < 80)
      || t.includes("encountered an issue")
    );
  };
  const kept = normalized
    .split(/(?<=[.!?])\s+/)
    .filter((s) => s.trim() && !isApologySentence(s));
  return kept.join(" ").trim();
}

export function formatAgentReply(text) {
  if (!text) return "";
  const cleaned = stripApologyBoilerplate(text);
  return cleaned
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\n/g, "<br />");
}

/** True when the assistant reply is a blocked geographic/jurisdiction validation message. */
export function isGeoValidationReply(text) {
  if (!text) return false;
  return /\byour answer was not saved\b/i.test(text);
}

export function countCapturedBySource(sections, captured) {
  let gate0 = 0;
  let doc = 0;
  let chat = 0;
  let inferred = 0;
  let pending = 0;

  (sections || []).forEach((s) => {
    (s.items || []).forEach((item) => {
      const cap = captured?.[item.id];
      if (!cap || isStaleUnlinkedCapture(item, cap, s.id)) {
        pending += 1;
        return;
      }
      const src = cap.source;
      if (src === "gate0") {
        gate0 += 1;
      } else if (src === "extracted") {
        doc += 1;
      } else if (src === "chat" || src === "manual" || src === "chat_paste") {
        chat += 1;
      } else if (src === "inferred") {
        inferred += 1;
      } else {
        chat += 1;
      }
    });
  });

  return { gate0, doc, chat, inferred, pending };
}

export function itemSourceLabel(cap) {
  if (!cap) return "Pending";
  if (cap.source === "gate0") return "Gate 0";
  if (cap.source === "extracted") return `Extracted T${cap.turn || "?"}`;
  return `Chat T${cap.turn || "?"}`;
}

/** Figma legend labels */
export function itemSourceBadge(cap) {
  if (!cap) return "Pending";
  if (cap.source === "inferred") return "Inferred";
  if (cap.source === "gate0") return "Gate 0";
  if (cap.source === "extracted") return "Doc";
  return "Chat";
}

/** Replace em/en dash or spaced hyphen separators with colon in question labels. */
export function formatFactQuestionText(text) {
  return String(text || "")
    .replace(/\s+[—–]\s+/g, " : ")
    .replace(/\s+-\s+/g, " : ")
    .trim();
}

export function parseIntakeStartedAt(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return {
    datePart: d.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    }),
    timePart: d.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }),
  };
}

export function countIntakeFacts(sections, captured) {
  let filled = 0;
  let total = 0;
  (sections || []).forEach((s) => {
    (s.items || []).forEach((item) => {
      total += 1;
      const cap = captured?.[item.id];
      if (cap && !isStaleUnlinkedCapture(item, cap, s.id)) filled += 1;
    });
  });
  const pct = total ? Math.round((filled / total) * 100) : 0;
  return { filled, total, pct };
}

export function countUserTurns(messages) {
  return (messages || []).filter((m) => m.role === "user" && !isSystemUserMessage(m)).length;
}

/** Two-letter avatar initials from the signed-in user profile. */
export function getUserInitials(user) {
  if (!user) return "?";
  const first = String(user.firstName || "").trim();
  const last = String(user.lastName || "").trim();
  if (first && last) {
    return `${first[0]}${last[0]}`.toUpperCase();
  }
  if (first) {
    return first.length >= 2
      ? first.slice(0, 2).toUpperCase()
      : first[0].toUpperCase();
  }
  const handle = String(user.username || user.email || "").trim();
  if (!handle) return "?";
  const namePart = handle.includes("@") ? handle.split("@")[0] : handle;
  const parts = namePart.split(/[._\s-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return namePart.slice(0, 2).toUpperCase();
}

export function getUserDisplayName(user) {
  if (!user) return "You";
  const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
  if (fullName) return fullName;
  if (user.username) return user.username;
  if (user.email) return user.email.split("@")[0];
  return "You";
}

/** Framework item id for app name (default A1). */
export function findAppNameItemId(sections) {
  for (const sec of sections || []) {
    for (const it of sec.items || []) {
      if (isActiveGate0Mapping(it, sec.id) && resolvesToAppNameField(it)) {
        return it.id;
      }
    }
  }
  for (const sec of sections || []) {
    for (const it of sec.items || []) {
      if (it.extraction_key === "app_name") return it.id;
    }
  }
  return "A1";
}

function resolvesToAppNameField(item) {
  const field = item?.gate0_field || gate0FieldForCanonicalText(item?.text);
  return field === "app_name";
}

/** True when App Identity auto-fill is active for this question.

 * Default template: canonical question text.
 * Custom templates: do not trust declared gate0_field alone — the agent decides
 * via semantic resolve; UI keeps agent-seeded source=gate0 answers separately.
 */
export function isActiveGate0Mapping(item, sectionId = null) {
  const text = (item?.text || "").trim();
  if (!text) return false;
  const field = gate0FieldForCanonicalText(text);
  if (!field) return false;
  return isTextCanonicalForGate0Field(text, field);
}

export function gate0FieldForItem(item) {
  const text = (item?.text || "").trim();
  const fromText = gate0FieldForCanonicalText(text);
  if (fromText) return fromText;
  // Prefer agent-resolved field stamped on the item only when present with capture.
  return item?.gate0_field || null;
}

/** True when App Identity actually supplied a value for this field. */
export function isGate0FieldFilled(field, gate0Data) {
  if (!field) return false;
  const raw = gate0Data?.[field];
  if (raw == null) return false;
  return String(raw).trim() !== "";
}

/**
 * True when this item is frozen from App Identity (value was provided there).
 * Linked-but-empty optional fields (e.g. Criticality skipped) stay editable in chat.
 */
export function isGate0FrozenItem(item, gate0Data, captured, sectionId = null) {
  const cap = captured?.[item?.id];
  if (cap?.source === "gate0" && String(cap.value ?? "").trim()) return true;
  if (!isActiveGate0Mapping(item, sectionId)) return false;
  return isGate0FieldFilled(gate0FieldForItem(item), gate0Data);
}

/** @deprecated Use isGate0FrozenItem for edit locks; this checks link only. */
export function isGate0MappedItem(item, sectionId = null) {
  return isActiveGate0Mapping(item, sectionId);
}

/** True when a captured answer no longer matches an unlinked framework question.
 *
 * Document-extracted / inferred answers are keyed by item_id. Do NOT drop them
 * solely because stored question text differs slightly from the live framework
 * (em-dash vs hyphen, punctuation, sync lag).
 *
 * Agent-seeded Gate 0 answers (source=gate0) are kept when non-empty even if the
 * frontend only knows default-template labels — custom/Healthcare wording is
 * resolved server-side via semantic mapping.
 */
export function isStaleUnlinkedCapture(item, cap, sectionId = null) {
  if (!cap || isActiveGate0Mapping(item, sectionId)) return false;
  const value = String(cap.value ?? "").trim();
  // Trust intake-agent App Identity seeds for custom template question text.
  if (cap.source === "gate0") {
    return !value;
  }
  // Extracted / inferred: keep whenever a non-empty value exists for this item_id.
  if (cap.source === "extracted" || cap.source === "inferred") {
    return !value;
  }
  const capQ = _normQuestionText(cap.text);
  const itemQ = _normQuestionText(item?.text);
  // Manual / chat: drop when the stored question no longer matches (question edited).
  return Boolean(capQ && itemQ && capQ !== itemQ);
}

function _normQuestionText(text) {
  return String(text || "")
    .trim()
    .replace(/[\u2010-\u2015\u2212]/g, "-")
    .replace(/\s+/g, " ")
    .toLowerCase();
}

/** Remove App Identity answers when the question text changed or link was removed.
 * Also refresh `text` on kept extracted captures so UI stays in sync with framework.
 */
export function stripStaleGate0Captured(captured, sections) {
  const next = { ...(captured || {}) };
  for (const sec of sections || []) {
    for (const it of sec.items || []) {
      if (!it.id) continue;
      if (isActiveGate0Mapping(it, sec.id)) {
        // Refresh question text on linked items that still hold a value
        if (next[it.id]?.value != null && String(next[it.id].value).trim() !== "") {
          next[it.id] = { ...next[it.id], text: it.text };
        }
        continue;
      }
      if (isStaleUnlinkedCapture(it, next[it.id], sec.id)) {
        delete next[it.id];
        continue;
      }
      // Keep extracted/inferred; sync question text to current framework item
      if (
        next[it.id]
        && (next[it.id].source === "extracted" || next[it.id].source === "inferred")
        && it.text
      ) {
        next[it.id] = { ...next[it.id], text: it.text };
      }
    }
  }
  return next;
}

/** Seed all active App Identity fields from gate0 data (except app_name — version handled below). */
function seedGate0FieldsIntoCaptured(next, sections, gate0Data) {
  const gate0 = gate0Data || {};
  for (const sec of sections || []) {
    for (const it of sec.items || []) {
      if (!isActiveGate0Mapping(it, sec.id)) continue;
      const field = it.gate0_field || gate0FieldForCanonicalText(it.text);
      if (!field || field === "app_name") continue;
      const raw = gate0[field];
      if (raw == null || String(raw).trim() === "") continue;
      if (next[it.id]?.source === "manual") continue;
      next[it.id] = {
        text: it.text,
        value: String(raw).trim(),
        source: "gate0",
        turn: next[it.id]?.turn ?? 0,
      };
    }
  }
}

/** Combine App Identity values into captured (all gate0 fields + app name/version). */
export function injectAppVersionIntoCaptured(captured, sections, gate0Data) {
  let next = stripStaleGate0Captured(captured, sections);
  seedGate0FieldsIntoCaptured(next, sections, gate0Data);
  let appNameItem = null;
  for (const sec of sections || []) {
    const found = (sec.items || []).find(
      (it) => isActiveGate0Mapping(it, sec.id) && resolvesToAppNameField(it)
    );
    if (found) {
      appNameItem = found;
      break;
    }
  }
  // Fallback: still restore A1 by extraction_key when App Identity has app_name.
  if (!appNameItem) {
    for (const sec of sections || []) {
      const found = (sec.items || []).find(
        (it) => it.extraction_key === "app_name" || resolvesToAppNameField(it)
      );
      if (found) {
        appNameItem = found;
        break;
      }
    }
  }
  if (!appNameItem) return next;
  const itemId = appNameItem.id;
  const appVersion = String(gate0Data?.app_version || gate0Data?.version || "").trim();
  const appName = String(gate0Data?.app_name || "").trim();
  if (!itemId || !appName) return next;
  if (next[itemId]?.source === "manual") return next;
  const current = String(next[itemId]?.value || "");
  const desired = appVersion && !/\bversion\b/i.test(appName)
    ? `${appName}, version ${appVersion}`
    : appName;
  // Keep a complete "name, version X" answer if already present; otherwise force Gate 0.
  if (current && /\bversion\b/i.test(current) && applicationNameForCard(current) === applicationNameForCard(desired)) {
    return next;
  }
  next[itemId] = {
    ...next[itemId],
    text: next[itemId]?.text || appNameItem.text || "App name and version",
    value: desired,
    source: "gate0",
  };
  return next;
}

/** Application name only — strips version suffix from A1 "App name and version" values. */
export function applicationNameForCard(raw) {
  let text = String(raw ?? "").trim();
  if (!text) return "";
  text = text.replace(/,\s*version\s+.+$/i, "");
  text = text.replace(/,\s*v[\d.]+.*$/i, "");
  text = text.replace(/\s+version\s+[\w.]+(?:\.\d+)*.*$/i, "");
  text = text.replace(/\s+v[\d.]+(?:\.\d+)*\s*$/i, "");
  return text.trim();
}

export function appNameFromCaptured(captured, sections) {
  const itemId = findAppNameItemId(sections);
  const raw = captured?.[itemId]?.value;
  if (raw == null || raw === "") return "";
  return applicationNameForCard(raw);
}
